import React from 'react';
import { Info, Layout, Target, ArrowUp, BookOpen, Square, Edit, Award, FileText } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const NailingTheCuriosityDrivenResume = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
        {/* Header */}
        <h1 className="text-4xl font-bold mb-8">Curiosity-Driven Resume Writing</h1>

        {/* Info Box */}
        <div className="bg-gray-100 p-6 rounded-lg mb-12">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 mt-1" />
            <div>
              <h2 className="font-medium mb-2">Essential Overview</h2>
              <p className="text-gray-700">
                Capture attention and secure interviews by crafting a resume that sparks curiosity. 
                A curiosity-driven resume invites the reader to learn more about your accomplishments 
                and potential.
              </p>
            </div>
          </div>
        </div>

        {/* What Is Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">1. What is Curiosity-Driven Resume Writing?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>It focuses on creating intrigue through concise yet impactful statements</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Rather than listing every detail, it highlights achievements that prompt questions and invite further discussion</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Why It Works */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Target className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">2. Why It Works</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Recruiters often scan resumes quickly. By providing compelling, bite-sized information, you encourage them to dig deeper</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>A resume that sparks curiosity can differentiate you from the competition and increase the likelihood of landing an interview</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Key Elements */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Layout className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">3. Key Elements of a Curiosity-Driven Resume</h3>
              
              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">a. Impactful Bullet Points</h4>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Use numbers, percentages, or other metrics to showcase results</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Avoid overly detailed explanations; instead, focus on outcomes</span>
                  </li>
                </ul>
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-medium text-gray-600 mb-2">Examples:</p>
                  <p className="text-gray-600 mb-2">"Boosted team productivity by 25% through workflow optimization"</p>
                  <p className="text-gray-600">"Generated $1M in new revenue by spearheading a product launch"</p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">b. Intriguing Accomplishments</h4>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Highlight unique achievements or standout contributions</span>
                  </li>
                </ul>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-gray-600">"Implemented a data-driven strategy that cut operational costs by 18%"</p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">c. Strategic Use of White Space</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Create a clean and uncluttered layout to draw attention to key points</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Ensure there is enough white space to avoid overwhelming the reader</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-blue-800">d. Selective Details</h4>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    <span>Leave some details unexplained to prompt follow-up questions</span>
                  </li>
                </ul>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-gray-600">"Developed a customer retention strategy that transformed client loyalty metrics"</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Writing Tips */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Edit className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">4. Tips for Writing a Curiosity-Driven Resume</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Focus on Results: Lead with outcomes and save the "how" for the interview</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Use Strong Action Verbs: Words like "spearheaded," "transformed," and "accelerated" convey impact</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Highlight Growth: Show how you've contributed to an organization's success</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Example Statements */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Award className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">5. Examples of Curiosity-Driven Statements</h3>
              <div className="space-y-6">
                <div>
                  <p className="font-medium text-red-600 mb-2">Instead of:</p>
                  <p className="text-gray-600 mb-2">"Managed a team of 10."</p>
                  <p className="font-medium text-green-600 mb-2">Use:</p>
                  <p className="text-gray-600">"Led a 10-person team to achieve a 30% increase in project efficiency."</p>
                </div>
                <div>
                  <p className="font-medium text-red-600 mb-2">Instead of:</p>
                  <p className="text-gray-600 mb-2">"Improved customer satisfaction."</p>
                  <p className="font-medium text-green-600 mb-2">Use:</p>
                  <p className="text-gray-600">"Elevated customer satisfaction scores by 15% through streamlined support processes."</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final Checks */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <FileText className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">6. Final Checks</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Review each section to ensure it sparks interest</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Have someone else read your resume and ask what they'd want to learn more about</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Closing Note */}
        <div className="bg-gray-100 p-6 rounded-lg">
          <p className="text-gray-700">
            By adopting a curiosity-driven approach, your resume will stand out and compel recruiters 
            to reach out. Let your achievements speak for themselves and invite the next step: the interview.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NailingTheCuriosityDrivenResume;